import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  Download, 
  RefreshCw, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  DollarSign, 
  Users, 
  CheckCircle,
  XCircle,
  CalendarIcon,
  Star,
  AlertTriangle
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { format, subMonths, startOfYear, endOfYear } from 'date-fns';
import { cn } from '@/lib/utils';
import { 
  getSupplierPerformanceData, 
  exportToCSV,
  type SupplierPerformanceData 
} from '@/services/reportService';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  ScatterChart,
  Scatter
} from 'recharts';

const SupplierPerformanceReport: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>(startOfYear(new Date()));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<string>('this-year');

  const { data: supplierData, isLoading, refetch } = useQuery({
    queryKey: ['supplier-performance', startDate, endDate],
    queryFn: () => getSupplierPerformanceData(startDate, endDate),
  });

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    const now = new Date();
    
    switch (range) {
      case 'last-30-days':
        setStartDate(subMonths(now, 1));
        setEndDate(now);
        break;
      case 'last-3-months':
        setStartDate(subMonths(now, 3));
        setEndDate(now);
        break;
      case 'last-6-months':
        setStartDate(subMonths(now, 6));
        setEndDate(now);
        break;
      case 'this-year':
        setStartDate(startOfYear(now));
        setEndDate(now);
        break;
      case 'last-year':
        const lastYear = new Date(now.getFullYear() - 1, 0, 1);
        setStartDate(lastYear);
        setEndDate(endOfYear(lastYear));
        break;
      default:
        break;
    }
  };

  const handleExportRankings = () => {
    if (!supplierData?.supplierRankings) return;
    exportToCSV(supplierData.supplierRankings, 'supplier_rankings');
  };

  const handleExportDelivery = () => {
    if (!supplierData?.deliveryPerformance) return;
    exportToCSV(supplierData.deliveryPerformance, 'supplier_delivery_performance');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getPerformanceColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (score: number) => {
    if (score >= 80) return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
    if (score >= 60) return <Badge className="bg-yellow-100 text-yellow-800">Good</Badge>;
    if (score >= 40) return <Badge className="bg-orange-100 text-orange-800">Fair</Badge>;
    return <Badge className="bg-red-100 text-red-800">Poor</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading supplier performance data...</p>
        </div>
      </div>
    );
  }

  const totalSuppliers = supplierData?.supplierRankings?.length || 0;
  const avgCompletionRate = supplierData?.supplierRankings?.reduce((sum, s) => sum + s.completion_rate, 0) / Math.max(totalSuppliers, 1) || 0;
  const totalValue = supplierData?.supplierRankings?.reduce((sum, s) => sum + s.total_value, 0) || 0;
  const avgDeliveryTime = supplierData?.supplierRankings?.reduce((sum, s) => sum + s.avg_delivery_time, 0) / Math.max(totalSuppliers, 1) || 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Supplier Performance Report</h2>
          <p className="text-muted-foreground">Analyze supplier rankings, delivery performance, and quality metrics</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last-30-days">Last 30 Days</SelectItem>
                <SelectItem value="last-3-months">Last 3 Months</SelectItem>
                <SelectItem value="last-6-months">Last 6 Months</SelectItem>
                <SelectItem value="this-year">This Year</SelectItem>
                <SelectItem value="last-year">Last Year</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {dateRange === 'custom' && (
              <div className="flex gap-2 items-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {format(startDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <span>to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {format(endDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Suppliers</p>
                <p className="text-2xl font-bold">{totalSuppliers}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Completion Rate</p>
                <p className="text-2xl font-bold">{avgCompletionRate.toFixed(1)}%</p>
              </div>
              <CheckCircle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold">{formatCurrency(totalValue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Delivery Time</p>
                <p className="text-2xl font-bold">
                  {avgDeliveryTime >= 0 ? '+' : ''}{avgDeliveryTime.toFixed(1)} days
                </p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="rankings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="rankings">Supplier Rankings</TabsTrigger>
          <TabsTrigger value="delivery">Delivery Performance</TabsTrigger>
          <TabsTrigger value="top-performers">Top Performers</TabsTrigger>
          <TabsTrigger value="improvements">Need Improvement</TabsTrigger>
        </TabsList>

        <TabsContent value="rankings" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Supplier Performance Rankings</CardTitle>
                <CardDescription>All suppliers ranked by quality score and performance metrics</CardDescription>
              </div>
              <Button onClick={handleExportRankings} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead className="text-right">Quality Score</TableHead>
                    <TableHead className="text-right">Completion Rate</TableHead>
                    <TableHead className="text-right">Rejection Rate</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                    <TableHead className="text-right">Procurements</TableHead>
                    <TableHead>Performance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplierData?.supplierRankings?.map((supplier, index) => (
                    <TableRow key={supplier.supplier_id}>
                      <TableCell className="font-medium">#{index + 1}</TableCell>
                      <TableCell className="font-medium">{supplier.supplier_name}</TableCell>
                      <TableCell className={cn("text-right font-semibold", getPerformanceColor(supplier.quality_score))}>
                        {supplier.quality_score.toFixed(1)}
                      </TableCell>
                      <TableCell className="text-right">{supplier.completion_rate.toFixed(1)}%</TableCell>
                      <TableCell className="text-right">{supplier.rejection_rate.toFixed(1)}%</TableCell>
                      <TableCell className="text-right">{formatCurrency(supplier.total_value)}</TableCell>
                      <TableCell className="text-right">{supplier.total_procurements}</TableCell>
                      <TableCell>{getPerformanceBadge(supplier.quality_score)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="delivery" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Delivery Performance Analysis</CardTitle>
                <CardDescription>On-time delivery rates and performance trends</CardDescription>
              </div>
              <Button onClick={handleExportDelivery} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Delivery Performance Chart */}
                <div>
                  <h4 className="text-lg font-semibold mb-4">On-Time vs Late Deliveries</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={supplierData?.deliveryPerformance || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="supplier_name" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="on_time_deliveries" fill="#22c55e" name="On-Time Deliveries" />
                      <Bar dataKey="late_deliveries" fill="#ef4444" name="Late Deliveries" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Delivery Performance Table */}
                <div>
                  <h4 className="text-lg font-semibold mb-4">Detailed Delivery Metrics</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier Name</TableHead>
                        <TableHead className="text-right">On-Time</TableHead>
                        <TableHead className="text-right">Late</TableHead>
                        <TableHead className="text-right">On-Time Rate</TableHead>
                        <TableHead className="text-right">Avg Delay (Days)</TableHead>
                        <TableHead>Trend</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {supplierData?.deliveryPerformance?.map((delivery) => {
                        const total = delivery.on_time_deliveries + delivery.late_deliveries;
                        const onTimeRate = total > 0 ? (delivery.on_time_deliveries / total) * 100 : 0;
                        
                        return (
                          <TableRow key={delivery.supplier_id}>
                            <TableCell className="font-medium">{delivery.supplier_name}</TableCell>
                            <TableCell className="text-right text-green-600">{delivery.on_time_deliveries}</TableCell>
                            <TableCell className="text-right text-red-600">{delivery.late_deliveries}</TableCell>
                            <TableCell className="text-right font-semibold">
                              {onTimeRate.toFixed(1)}%
                            </TableCell>
                            <TableCell className="text-right">
                              {delivery.avg_delay_days > 0 ? delivery.avg_delay_days.toFixed(1) : '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {delivery.performance_trend === 'improving' && (
                                  <>
                                    <TrendingUp className="h-4 w-4 text-green-500" />
                                    <span className="text-green-600 text-sm">Improving</span>
                                  </>
                                )}
                                {delivery.performance_trend === 'declining' && (
                                  <>
                                    <TrendingDown className="h-4 w-4 text-red-500" />
                                    <span className="text-red-600 text-sm">Declining</span>
                                  </>
                                )}
                                {delivery.performance_trend === 'stable' && (
                                  <span className="text-gray-600 text-sm">Stable</span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="top-performers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Performing Suppliers</CardTitle>
              <CardDescription>Suppliers with the highest quality scores and performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {supplierData?.topPerformers?.map((supplier, index) => (
                  <div key={supplier.supplier_id} className="flex items-center justify-between p-4 border rounded-lg bg-green-50">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Star className="h-5 w-5 text-yellow-500" />
                        <span className="text-lg font-semibold">#{index + 1}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-lg">{supplier.supplier_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {supplier.total_procurements} procurements • {formatCurrency(supplier.total_value)} total value
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">
                        {supplier.quality_score.toFixed(1)}
                      </div>
                      <div className="text-sm text-muted-foreground">Quality Score</div>
                      <div className="text-sm">
                        {supplier.completion_rate.toFixed(1)}% completion rate
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="improvements" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Suppliers Needing Improvement</CardTitle>
              <CardDescription>Suppliers with lower performance scores requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {supplierData?.underPerformers && supplierData.underPerformers.length > 0 ? (
                <div className="grid gap-4">
                  {supplierData.underPerformers.map((supplier, index) => (
                    <div key={supplier.supplier_id} className="flex items-center justify-between p-4 border rounded-lg bg-red-50">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-red-500" />
                          <span className="text-lg font-semibold">#{supplierData.supplierRankings.length - index}</span>
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">{supplier.supplier_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {supplier.total_procurements} procurements • {formatCurrency(supplier.total_value)} total value
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-red-600">
                          {supplier.quality_score.toFixed(1)}
                        </div>
                        <div className="text-sm text-muted-foreground">Quality Score</div>
                        <div className="text-sm">
                          {supplier.completion_rate.toFixed(1)}% completion rate
                        </div>
                        <div className="text-sm text-red-600">
                          {supplier.rejection_rate.toFixed(1)}% rejection rate
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">All suppliers are performing well!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplierPerformanceReport;